import React from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { fetchExtenderPop } from "./requests/request";
import { type CpeRole, cpeRolePresentation } from "./ExtenderPopData";
import type {
  ExtenderPopDetail,
  RouterLink,
  Subscription,
} from "./extenderPopDetail.types";

/**
 * Pieces the Extended PoP detail and the CPE detail share: one fetch of the
 * site (the CPE view reads its CPE out of it, from the same cache entry, so
 * going from the site to a CPE paints at once) and the small formatters.
 */

export const useExtenderPopDetail = (extenderPopId: number, tenantId: number) => {
  const api = useAxiosAuth0();
  const hasValidId = Number.isInteger(extenderPopId) && extenderPopId > 0;
  // The last answer for this PoP paints at once when the user comes back (a
  // view switch, Back); the request still runs and replaces it.
  const fetched = useCachedFetch<ExtenderPopDetail>(
    hasValidId
      ? `kontroledge/extender-pops/${extenderPopId}?tenantId=${tenantId}`
      : null,
    async () => {
      const response = await fetchExtenderPop(api, extenderPopId, tenantId);
      if (!response.data?.success || !response.data.data) {
        throw new Error(response.data?.message || "Could not load the details");
      }
      return response.data.data as ExtenderPopDetail;
    }
  );
  return {
    ...fetched,
    error: hasValidId ? fetched.error : "Missing Extended PoP id",
  };
};

export const LoadingCard: React.FC = () => (
  <GenericCard
    isCollapsible={false}
    bodyContent={
      <div className="flex flex-col gap-3 py-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonLine key={i} className="h-4 w-full" />
        ))}
      </div>
    }
  />
);

export const LoadingDetail: React.FC = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 px-4">
    <LoadingCard />
    <LoadingCard />
  </div>
);

// The picker path of EditableTagList never reaches the legacy handlers; they
// exist only to satisfy the combined prop contract.
export const legacyTagsNoop = async () => undefined;

/**
 * Location like the Ports POP cell. The inventory displayName already
 * carries the human location ("Televisa SNA (San Angel, MX)"), so nothing
 * is appended to it.
 */
export const formatLocation = (site: ExtenderPopDetail["site"]): string | null =>
  site?.displayName || site?.name || null;

export const formatTerm = (subscription: Subscription | null): string | null =>
  subscription?.term
    ? `${subscription.term} ${subscription.term_unit ?? ""}`.trim()
    : null;

export const formatRenewDate = (subscription: Subscription | null): string | null =>
  subscription?.renewDate && subscription.renewDate !== "N/A"
    ? new Date(subscription.renewDate).toLocaleDateString()
    : null;

/** Chart / table label of a link: destination, carrier and speed. */
export const linkLabel = (link: RouterLink): string =>
  link.kind === "ha-interconnect"
    ? `HA interconnect${link.capacity ? ` (${link.capacity})` : ""}`
    : `Uplink${link.destination ? ` — ${link.destination}` : ""}${
        link.carrierName || link.capacity
          ? ` (${[link.carrierName, link.capacity].filter(Boolean).join(", ")})`
          : ""
      }`;

export const RoleTag: React.FC<{ role: CpeRole }> = ({ role }) => {
  const presentation = cpeRolePresentation(role);
  return presentation ? (
    <GenericTag
      variant={presentation.variant}
      label={presentation.label}
      size="small"
    />
  ) : null;
};
